import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { BaseComponent } from "../../shared/base.component";
import { ApiService } from "src/app/services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { PlanRegion } from "src/app/generated-models/PlanRegion";
import { PreprocessDateTime } from "src/app/utility/view-field-mapping";
import { ModalService } from "src/app/shared/modal.service";
import { PlanRegionOverallApproval } from "src/app/generated-models/PlanRegionOverallApproval";
import { GridApi, GridOptions , GridReadyEvent } from "ag-grid-community";
import { extendDefaultOptions } from "src/app/utility/gridHelper";
import { getCapacityOrderColumnDefinition, getPlanRegionApprovalsColumnDefinition, PlansDiffColumnDefinition } from "./ag-grid-column-definition";
import { PlanRegionDiff } from "src/app/generated-models/PlanRegionDiff";
import { PlanRegionStatusEnum } from "src/app/shared/enums/plan-enums";
import { PlanRejectData } from "src/app/generated-models/PlanRejectData";
import { IUserProfile } from "src/app/npr-request.model";
import { CapacityOrder } from "src/app/generated-models/CapacityOrder";
import { PlanRegionApproval } from "../../generated-models/PlanRegionApproval";
import { Region } from "src/app/generated-models";
import { Alternative } from "../../generated-models/Alternative";
import { UpdateAlternative } from "../../generated-models/UpdateAlternative";
import { HttpErrorResponse } from "@angular/common/http";
import { PlanRegionWithApprovalStatus } from "../../generated-models/PlanRegionWithApprovalStatus";

@Component({
  standalone: false,
  templateUrl: "./plan-review.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})

export class PlanReviewComponent extends BaseComponent implements AfterViewInit, OnInit {
  gridOptions: GridOptions;
  gridApi: GridApi;
  capacityOrderGridOptions: GridOptions;
  capacityOrderGridApi: GridApi;
  planRegionApprovalsGridOptions: GridOptions;
  planRegionApprovalsGridApi: GridApi;
  planDiffRowData: PlanRegionDiff[] = [];
  capacityOrderRowData: CapacityOrder[] = [];
  planRegionApprovalsRowData: PlanRegionApproval[] = [];
  region: Region;

  serviceTreeId: string;
  regionName: string;
  version: number;
  blueprint: string;
  planRegion: PlanRegion;
  latestVersion: number = null;
  planDetailLink: string;
  planLatestVersionReviewLink: string;
  message: string;
  contactEmail: string;
  canApprove = false;
  canReject = false;
  showRejectComment = false;
  userProfile: IUserProfile;
  isAdmin = false;
  isOperator = false;
  isGCTApprover = false;

  get isAdminOrOperator(): boolean {
    return this.isAdmin || this.isOperator;
  }
  readonly serviceType = "VM";
  alternatives: Alternative[];
  alternativeMap: { [key: string]: string[] } = {};
  alternativeAssignedMap: { [key: string]: string } = {};
  alternativeOriginals: string[] = [];
  isGABlueprint = false;
  fulfillmentStarted = false;
  showActionButtons = false;

  // Add as class members in PlanReviewComponent
  @ViewChild("commentSection", { static: false }) commentSection: ElementRef;
  selectedCommentTopic: string = null;

  constructor(
    private readonly route: ActivatedRoute,
    private apiService: ApiService,
    protected modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private title: Title,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ngOnInit() {
    this.gridOptions = extendDefaultOptions({
      columnDefs: PlansDiffColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });

    this.capacityOrderGridOptions = extendDefaultOptions({
      columnDefs: getCapacityOrderColumnDefinition(this.apiService),
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });

    this.planRegionApprovalsGridOptions = extendDefaultOptions({
      columnDefs: getPlanRegionApprovalsColumnDefinition(this.ShowWhoCanApproveDialog.bind(this)),
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });

    this.serviceTreeId = this.route.snapshot.params["serviceTreeId"];
    this.regionName = this.route.snapshot.params["region"];
    this.blueprint = this.route.snapshot.params["blueprint"];
    this.isGABlueprint = this.blueprint == "GA";
    if (this.route.snapshot.params.hasOwnProperty("version")) {
      this.version = this.route.snapshot.params["version"];
    }
    else {
      try {
        const latestVersionPlanRegion = await this.apiService
          .getPlanRegionLatestVersion(this.serviceTreeId, this.regionName, this.blueprint)
          .toPromise();
        this.version = latestVersionPlanRegion.Version;
      } catch (e) {
        this.notificationService.error(e);
      }
    }
    this.title.setTitle(`${this.route.snapshot.data.title} | ${this.serviceTreeId} | ${this.blueprint} v${this.version} | ${this.regionName}`);
    this.planDetailLink = `/quota/plans/services/${this.serviceTreeId}/regions/${this.regionName}/versions/${this.version}/blueprints/${this.blueprint}/detail`

    this.loadingService.setLoading(true);
    this.userProfile = await this.apiService.getUserProfile();
    if (this.userProfile?.IsAdmin) {
      this.isAdmin = true;
    }
    if (this.userProfile?.IsOperator) {
      this.isOperator = true;
    }
    if (this.userProfile?.IsGCTApprover) {
      this.isGCTApprover = true;
    }

    await this.loadPlan();
    await this.loadPlanDiff();
    await this.loadPlanRelatedCapacityOrders();
    await this.loadPlanRegionApprovals();
    await this.loadPlanRegionStatus();
    await this.loadAlternatives();
    this.loadingService.setLoading(false);
    this.cdr.detectChanges();

    setTimeout(() => {
      this.gridApi.autoSizeAllColumns();
      this.capacityOrderGridApi.autoSizeAllColumns();
      if (this.planRegionApprovalsGridApi) {
        this.planRegionApprovalsGridApi.autoSizeAllColumns();
      }
    }, 100);

    // Add event listener for navigating to comment topic
    document.addEventListener("navigateToCommentTopic", this.handleNavigateToCommentTopic.bind(this));
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  onCapacityOrderGridReady(params: GridReadyEvent) {
    this.capacityOrderGridApi = params.api;
  }

  onPlanRegionApprovalsGridReady(params: GridReadyEvent) {
    this.planRegionApprovalsGridApi = params.api;
  }

  async loadPlan() {
    try {
      const planRegion = await this.apiService
        .getPlanRegion(this.serviceTreeId, this.regionName, this.version, this.blueprint)
        .toPromise();
      this.planRegion = planRegion;
      this.showActionButtons = false;

        if (this.planRegion.Status === PlanRegionStatusEnum.Approvable) {
            this.showActionButtons = true;
            this.canApprove = this.userProfile?.IsApprover;
            this.canReject = this.userProfile?.IsAdmin;
        }

      if (this.planRegion.Status == PlanRegionStatusEnum.Rejected && this.planRegion.RejectComment) {
        this.showRejectComment = true;
      }

      var detail = await this.apiService
        .getPlanRegionDetail(this.serviceTreeId, this.regionName, this.version, this.blueprint)
        .toPromise();
      this.contactEmail = detail.ContactEmail;

      const latestVersionPlanRegion = await this.apiService
        .getPlanRegionLatestVersion(this.serviceTreeId, this.regionName, this.blueprint)
        .toPromise();

      var regions = await this.apiService
        .getRegionList()
        .toPromise();
      this.region = regions.find(r => r.RegionName == this.regionName);

      this.latestVersion = latestVersionPlanRegion.Version;
      if (this.latestVersion != null) {
        this.planLatestVersionReviewLink = `/quota/plans/services/${this.serviceTreeId}/regions/${this.regionName}/versions/${this.latestVersion}/blueprints/${this.blueprint}/review`
      }
    }
    catch (e) {
      this.notificationService.error(e);
    }
  }

  async loadPlanDiff() {
    return await this.apiService
      .getRegionalPlanDiff(this.serviceTreeId, this.regionName, this.version, this.blueprint)
      .toPromise()
      .then((response) => {
        this.planDiffRowData = response;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadPlanRelatedCapacityOrders() {
    return await this.apiService
      .getRegionalPlanRelatedCapacityOrders(this.serviceTreeId, this.regionName, this.version, this.blueprint)
      .toPromise()
      .then((response) => {
        this.capacityOrderRowData = response;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadPlanRegionApprovals() {
    return await this.apiService
      .getRegionalPlanApprovals(this.serviceTreeId, this.regionName, this.version, this.blueprint)
      .toPromise()
      .then((response) => {
        this.planRegionApprovalsRowData = response;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async showApprovePlanDialog() {
    this.approvePlan();
  }

  ShowWhoCanApproveDialog(type: string) {
    this.modalService.resourceApproversModal(type, this.regionName);
  }

  approvePlan() {
    const planRegionOverallApproval: PlanRegionOverallApproval = {
      ApprovedVersion: this.planRegion.Version,
      Blueprint: this.planRegion.Blueprint,
    } as PlanRegionOverallApproval;

    this.loadingService.setLoading(true);

    this.apiService.approvePlanRegion(this.planRegion.ServiceTreeId, this.planRegion.Region, planRegionOverallApproval).subscribe(
      (response) => {
        if (response) {
          this.message = response.Message;
        } else {
          this.message = "Plan have been approved successfully.";
        }

        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err) => {
        this.message = `Failed to approve plan. Server response: ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      async () => {
        this.loadingService.setLoading(true);
        await this.loadPlan();
        await this.loadPlanDiff();
        await this.loadPlanRegionApprovals();
        this.loadingService.setLoading(false);
      }
    );
  }

  // for test now
  syncExecutionPlan() {
    this.loadingService.setLoading(true);

    this.apiService.syncExecutionPlan().subscribe(
      (response) => {
        if (response) {
          this.message = response.Message;
        } else {
          this.message = "Succeed";
        }

        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err) => {
        this.message = `Failed to test sync execution plan. Server response: ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      async () => {
        this.loadingService.setLoading(true);
        this.loadingService.setLoading(false);
      }
    );
  }

  async showRejectPlanDialog() {
    const comment = await this.modalService.planRejectModal();
    await this.rejectPlan(comment);
  }

  rejectPlan(comment: string) {
    const planRejectData: PlanRejectData = {
      Version: this.planRegion.Version,
      Blueprint: this.planRegion.Blueprint,
      Comment: comment,
    } as PlanRejectData;

    this.loadingService.setLoading(true);

    this.apiService.rejectPlanRegion(this.planRegion.ServiceTreeId, this.planRegion.Region, planRejectData).subscribe(
      () => {
        this.message = "Plan have been rejected.";
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err) => {
        this.message = `Failed to reject plan. Server response: ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      async () => {
        this.loadingService.setLoading(true);
        await this.loadPlan();
        await this.loadPlanDiff();
        await this.loadPlanRegionApprovals();
        this.loadingService.setLoading(false);
      }
    );
  }

  showStatus(planRegion: PlanRegion) {
    if (planRegion.Status === PlanRegionStatusEnum.Ready) {
      return `Approved by ${planRegion.ApprovedBy} at ${PreprocessDateTime(planRegion.ApprovedTime)} - Ready to Fulfill`;
    } else if (planRegion.Status === PlanRegionStatusEnum.Approved) {
      return `Approved by ${planRegion.ApprovedBy} at ${PreprocessDateTime(planRegion.ApprovedTime)}`;
    } else if (planRegion.Status === PlanRegionStatusEnum.Rejected) {
      return `Rejected by ${planRegion.RejectedBy} at ${PreprocessDateTime(planRegion.RejectedTime)}`;
    } else {
      return planRegion.Status;
    }
  }

  /**
   * Determines whether to show the fulfillment documentation link.
   * The link is only shown for 'Ready' status to guide users on next steps for fulfillment.
   */
  shouldShowFulfillmentLink(): boolean {
    return this.planRegion?.Status === 'Ready';
  }

  showSubmit(planRegion: PlanRegion) {
    if (planRegion.Submitter && planRegion.SubmitTime) {
      return `by ${planRegion.Submitter} at ${PreprocessDateTime(planRegion.SubmitTime)}`;
    } else {
      return "N/A";
    }
  }

  async loadPlanRegionStatus() {
    return await this.apiService.getPlanRegionStatus(this.regionName, this.serviceTreeId, this.blueprint)
      .toPromise()
      .then((response) => {
        this.fulfillmentStarted = response.SubscriptionCompleted > 0;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadAlternatives() {
    return await this.apiService
      .getAlternatives(this.serviceTreeId, this.regionName, this.blueprint, this.version)
      .toPromise()
      .then((response) => {
        this.alternatives = response;;
        this.alternatives.forEach(item => {
          this.alternativeMap[item.Original] = [item.Original, ...item.Alternatives.filter(i => i != item.Original)];
          this.alternativeAssignedMap[item.Original] = item.Assigned ?? item.Original;
          this.alternativeOriginals.push(item.Original);
        });
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  UpdateAlternativeValue(original: string, requireSkuAvailableInCrp: boolean = true) {
    const updateAlternative: UpdateAlternative = {
      Type: this.serviceType,
      Original: original,
      Assigned: this.alternativeAssignedMap[original],
      RequireSkuAvailableInCrp: requireSkuAvailableInCrp
    } as UpdateAlternative;

    this.UpdateAlternativeInternal(updateAlternative);
  }

  UpdateAlternativeInternal(updateAlternative: UpdateAlternative) {
    this.apiService
      .updateAlternative(this.serviceTreeId, this.regionName, this.version, updateAlternative)
      .subscribe(
        () => {
          this.notificationService.info(`the alternative of ${updateAlternative.Original} has been assigned ${updateAlternative.Assigned} successfully`);
        },
        (error: HttpErrorResponse) => {
          this.modalService.planAlternativeModal(error).then(
            (requireSkuAvailableInCrp) => {
              if (requireSkuAvailableInCrp == false) {
                this.UpdateAlternativeValue(updateAlternative.Original, requireSkuAvailableInCrp);
              }
            }
          );
        }
      );
  }

  async removePlan() {
    if (!this.planRegion) {
      return;
    }
    const planRegionsToRemove = [{
      ServiceTreeId: this.serviceTreeId,
      Region: this.regionName,
      Version: this.version,
      Blueprint: this.blueprint,
      Status: this.planRegion?.Status
    }] as PlanRegionWithApprovalStatus[];
    await this.modalService.planSetRemovedModal(planRegionsToRemove);
    await this.loadPlan();
  }

  ngOnDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener("navigateToCommentTopic", this.handleNavigateToCommentTopic.bind(this));

    // Keep any existing destroy logic
  }

  /**
   * Handles navigation to comment topic from approval grid
   * @param event Custom event containing the resource name to use as topic
   */
  handleNavigateToCommentTopic(event: CustomEvent) {
    const resource = event.detail;
    if (resource) {
      this.selectedCommentTopic = resource;

      // Scroll to comment section
      setTimeout(() => {
        if (this.commentSection?.nativeElement) {
          this.commentSection.nativeElement.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }

  /**
 * Called when a comment is added to notify the user
 */
  onCommentAdded() {
    this.notificationService.info("Comment added successfully");
  }
}
